import React, { useState, useEffect } from "react";
import {
  Controller,
  useFormContext,
  FieldError,
  FieldValues,
  RegisterOptions
} from "react-hook-form";
import Input, { IProps } from "../Input";
import clsx from "clsx";

interface DollarInputProps<T extends FieldValues> extends IProps<T> {
  label?: string | React.ReactNode;
  className?: string;
  inputClass?: string;
  labelClass?: string;
  required?: boolean;
  disabled?: boolean;
  error?: FieldError;
  rules?: RegisterOptions;
}

const formatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const DollarInput = <T extends FieldValues>({
  name,
  label,
  className,
  inputClass,
  labelClass,
  required = false,
  disabled = false,
  onChange: handleOnChange,
  onBlur: handleOnBlur,
  rules,
  ...props
}: DollarInputProps<T>) => {
  const { control } = useFormContext();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");

  return (
    <Controller
      name={name as string}
      control={control}
      rules={rules}
      render={({ field: { onChange, onBlur, value }, fieldState: { error: fieldError } }) => {
        useEffect(() => {
          if (value !== undefined && value !== null && !isFocused) {
            setInputValue(formatter.format(value));
          } else if (value !== undefined && value !== null && isFocused) {
            setInputValue(value.toString());
          } else {
            setInputValue("");
          }
        }, [value, isFocused]);

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = e.target.value;
          const numericString = rawValue.replace(/[^0-9.]/g, "");
          const numericValue = parseFloat(numericString);
          handleOnChange && handleOnChange(e);
          if (!isNaN(numericValue)) {
            onChange(numericValue);
            setInputValue(rawValue);
          } else {
            onChange(0);
            setInputValue("");
          }
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
          setIsFocused(false);
          if (value !== undefined && value !== null && !isNaN(value)) {
            setInputValue(formatter.format(value));
          }
          onBlur();
          handleOnBlur && handleOnBlur(e);
        };

        const handleFocus = () => {
          setIsFocused(true);
          setInputValue(value !== undefined && value !== null ? value.toString() : "");
        };

        return (
          <Input
            {...props}
            type="text"
            inputMode="decimal"
            label={label}
            labelClass={labelClass}
            className={clsx(className)}
            prefixUnit="&nbsp;$"
            disabled={disabled}
            inputClass={clsx(inputClass)}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            error={fieldError}
            required={required}
          />
        );
      }}
    />
  );
};

export default DollarInput;
